import { createModal, StaticModal } from '@nexelem/modals';
import { useCustomTranslation } from '../../../../../hooks';
import { Banner, Button } from '@nexelem/components';
import { FormProvider, useForm } from 'react-hook-form';
import { FormCalendarInput } from '@nexelem/form-components';
import { ShiftSelector } from '../../../ShiftSelector';
import { LemmaMachineAreaSelect } from './LemmaMachineAreaSelect';
import { zodResolver } from '@hookform/resolvers/zod';
import { BrigadeSelect } from './BrigadeSelect';
import { Shift } from '@nexelem/config';
import { SyntheticEvent, useState } from 'react';
import { TabContext } from '@mui/lab';
import { DetailsTab, DetailsTabList } from '@nexelem/tabs';
import { AssignmentConfigTab } from './AssignmentConfigTab';
import { AssignmentFormValues, assignmentFormSchema, useAssignWorkersSubmit } from './useAssignWorkersSubmit';
import { useFaro } from '@nexelem/grafana';

enum AutoAssignmentModalTab {
  ASSIGNMENT = 'assignment',
  RANKING = 'ranking',
}

const AutoAssignmentModal = () => {
  const faro = useFaro();
  const { t, tWrapper } = useCustomTranslation('products.productionPlan.autoAssigment');
  const [selectedShifts, setSelectedShifts] = useState<Shift['nameKey'][]>([]);
  const [isBannerOpen, setIsBannerOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<AutoAssignmentModalTab>(AutoAssignmentModalTab.ASSIGNMENT);
  const { submitAssignment, isSubmitting } = useAssignWorkersSubmit(selectedShifts);

  const form = useForm<AssignmentFormValues>({
    resolver: zodResolver(assignmentFormSchema),
  });

  const handleTabChange = (_: SyntheticEvent, tab: AutoAssignmentModalTab) => {
    setActiveTab(tab);
    if (tab === AutoAssignmentModalTab.RANKING) {
      faro?.api.pushEvent('auto_assignment_event', {
        type: 'button_click',
        button_name: 'ranking_tab_click',
      });
    }
  };

  return (
    <FormProvider {...form}>
      <StaticModal
        component="form"
        maxWidth="lg"
        onClose={closeAutoAssigmentModal}
        onSubmit={form.handleSubmit(submitAssignment)}
      >
        <StaticModal.Header withCloseButton>{tWrapper('assignManyHeader')}</StaticModal.Header>
        <StaticModal.Content className="flex flex-col gap-6">
          <TabContext value={activeTab}>
            <DetailsTabList variant="fullWidth" onChange={handleTabChange}>
              <DetailsTab label={tWrapper('tabs.assignment')} value={AutoAssignmentModalTab.ASSIGNMENT} />
              <DetailsTab label={tWrapper('tabs.config')} value={AutoAssignmentModalTab.RANKING} />
            </DetailsTabList>
            {activeTab === AutoAssignmentModalTab.ASSIGNMENT ? (
              <>
                {isBannerOpen && (
                  <Banner withCloseButton message={tWrapper('assignManyInfo')} onClose={() => setIsBannerOpen(false)} />
                )}
                <p className="text-s18">{tWrapper('chooseAreaAndBrigade')}</p>
                <LemmaMachineAreaSelect />
                <BrigadeSelect />
                <FormCalendarInput required className="max-w-56" label={t('common.date')} name="date" />
                <ShiftSelector selectedShifts={selectedShifts} setSelectedShifts={setSelectedShifts} />
              </>
            ) : (
              <AssignmentConfigTab />
            )}
          </TabContext>
        </StaticModal.Content>
        <StaticModal.Footer>
          <Button variant="outlined" onClick={closeAutoAssigmentModal}>
            {t('common.cancel')}
          </Button>
          <Button isLoading={isSubmitting} type="submit" variant="contained">
            {tWrapper('assignWorkers')}
          </Button>
        </StaticModal.Footer>
      </StaticModal>
    </FormProvider>
  );
};

export const { openAutoAssigmentModal, closeAutoAssigmentModal } = createModal(AutoAssignmentModal, {
  name: 'AutoAssigmentModal',
});
